import React, { useState } from 'react';
import { 
  Container, 
  Tabs, 
  Tab, 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  TextField, 
  Button, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Card, 
  CardContent, 
  CardHeader, 
  Divider,
  useTheme,
  useMediaQuery,
  InputAdornment,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  alpha,
  Avatar
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalculateIcon from '@mui/icons-material/Calculate';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PercentIcon from '@mui/icons-material/Percent';
import SchoolIcon from '@mui/icons-material/School';
import ReceiptIcon from '@mui/icons-material/Receipt';

const GSTCalculator = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [amount, setAmount] = useState('');
  const [gstRate, setGstRate] = useState('');
  const [gstAmount, setGstAmount] = useState(null);
  const [totalAmount, setTotalAmount] = useState(null);
  const [isInclusive, setIsInclusive] = useState(false);
  const [calculationType, setCalculationType] = useState('exclusive');
  const [expandedAccordion, setExpandedAccordion] = useState('');
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));

  const gstRates = [
    { category: 'Standard Rate', rate: '18%', cgst: '9%', sgst: '9%', igst: '18%' },
    { category: 'Lower Rate', rate: '5%', cgst: '2.5%', sgst: '2.5%', igst: '5%' },
    { category: 'Higher Rate', rate: '28%', cgst: '14%', sgst: '14%', igst: '28%' },
    { category: 'Special Rate', rate: '12%', cgst: '6%', sgst: '6%', igst: '12%' },
    { category: 'Essential Items', rate: '0%', cgst: '0%', sgst: '0%', igst: '0%' },
    { category: 'Precious Metals', rate: '3%', cgst: '1.5%', sgst: '1.5%', igst: '3%' },
  ];

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : '');
  };

  const calculateGST = () => {
    if (!amount || !gstRate || isNaN(amount) || isNaN(gstRate)) {
      setGstAmount(null);
      setTotalAmount(null);
      return;
    }

    const rate = parseFloat(gstRate);
    const parsedAmount = parseFloat(amount);

    if (calculationType === 'inclusive') {
      const baseAmount = (parsedAmount * 100) / (100 + rate);
      const gst = parsedAmount - baseAmount;
      setGstAmount(gst);
      setTotalAmount(parsedAmount);
    } else {
      const gst = (parsedAmount * rate) / 100;
      setGstAmount(gst);
      setTotalAmount(parsedAmount + gst);
    }
  };

  const handleCalculationTypeChange = (event) => {
    setCalculationType(event.target.value);
  };

  const handleCalculateClick = () => {
    calculateGST();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderHeroSection = () => (
    <Box
      component={Paper}
      elevation={0}
      sx={{
        bgcolor: 'primary.main',
        color: 'white',
        py: { xs: 4, sm: 6, md: 8 },
        mb: 4,
        borderRadius: 0,
        backgroundImage: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <Typography 
                variant="h2" 
                component="h1" 
                sx={{ 
                  fontWeight: 800,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                  mb: 2,
                  textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                GST Calculator & Knowledge Hub
              </Typography>
              <Typography 
                variant="h6"
                sx={{ 
                  mb: 3, 
                  maxWidth: '600px',
                  lineHeight: 1.6,
                  fontWeight: 400,
                  opacity: 0.9
                }}
              >
                Effortlessly calculate GST amounts and expand your knowledge about India's Goods & Services Tax system. All the resources you need in one place.
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => window.scrollTo({ top: 400, behavior: 'smooth' })}
                sx={{ 
                  fontWeight: 600,
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0 6px 12px rgba(0,0,0,0.3)',
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Get Started
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box 
              sx={{ 
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              <Box 
                sx={{ 
                  width: 300,
                  height: 300,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.1)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: '0 0 40px rgba(0,0,0,0.1)'
                }}
              >
                <CalculateIcon sx={{ fontSize: 120, opacity: 0.9 }} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
      {/* Decorative elements */}
      <Box sx={{ 
        position: 'absolute',
        width: 200,
        height: 200,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.05)',
        top: '-50px',
        right: '-50px'
      }} />
      <Box sx={{ 
        position: 'absolute',
        width: 120,
        height: 120,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.08)',
        bottom: '-30px',
        left: '10%'
      }} />
    </Box>
  );

  return (
    <>
      {renderHeroSection()}
      
      <Container sx={{ py: 4 }}>
        <Paper 
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            mb: 6
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant={isMedium ? "fullWidth" : "standard"}
            centered
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: theme.palette.background.paper,
              '& .MuiTabs-indicator': {
                backgroundColor: 'secondary.main',
                height: '4px',
                borderRadius: '2px',
              },
              '& .MuiTab-root': {
                fontWeight: 700,
                textTransform: 'none',
                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                color: 'text.secondary',
                transition: 'all 0.3s',
                py: 3,
                '&:hover': {
                  color: 'secondary.main',
                  backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                },
                '&.Mui-selected': {
                  color: 'secondary.main',
                },
              },
            }}
          >
            <Tab 
              icon={<SchoolIcon />} 
              iconPosition="start" 
              label={isMobile ? "Knowledge" : "GST Knowledgebase"} 
            />
            <Tab 
              icon={<CalculateIcon />} 
              iconPosition="start" 
              label={isMobile ? "Calculator" : "GST Calculator"} 
            />
          </Tabs>

          {activeTab === 0 && (
            <Box
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 700, 
                    color: 'primary.main',
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' }
                  }}
                >
                  GST Knowledge Center
                </Typography>
                <Typography 
                  sx={{ 
                    color: 'text.secondary', 
                    maxWidth: '700px', 
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  Expand your understanding of India's Goods and Services Tax system with our comprehensive guides
                </Typography>
                <Divider sx={{ maxWidth: '150px', mx: 'auto', mb: 4, borderColor: 'secondary.main', borderBottomWidth: 3 }} />
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Accordion 
                    expanded={expandedAccordion === 'panel1'} 
                    onChange={handleAccordionChange('panel1')}
                    sx={{
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      '&:before': { display: 'none' },
                      mb: 2,
                      overflow: 'hidden',
                      '&.Mui-expanded': {
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                      },
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ 
                        backgroundColor: expandedAccordion === 'panel1' ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: 'primary.main', 
                            mr: 2,
                            width: 36,
                            height: 36
                          }}
                        >
                          <ReceiptIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          What is GST?
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: { xs: 3, md: 4 } }}>
                      <Typography sx={{ mb: 2, lineHeight: 1.7 }}>
                        Goods and Services Tax (GST) is a comprehensive, multi-stage, destination-based tax that is levied on every value addition. It was implemented on 1st July 2017 with the purpose of "One Nation, One Tax." GST replaced multiple indirect taxes like VAT, excise, and service tax.
                      </Typography>
                      <Typography sx={{ mb: 2, lineHeight: 1.7 }}>
                        The GST system features a seamless input tax credit flow, where businesses can claim credit for the taxes paid on inputs across the entire supply chain, which helps eliminate the cascading effect (tax on tax).
                      </Typography>
                      <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1, color: 'success.dark' }}>
                          Have any doubt or question?
                        </Typography>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<WhatsAppIcon />}
                          href="https://wa.me/+919455133862?text=Hi!%20I%20have%20a%20question%20about%20GST%20introduction."
                          target="_blank"
                          sx={{ 
                            mt: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2
                          }}
                        >
                          Ask on WhatsApp
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion 
                    expanded={expandedAccordion === 'panel2'} 
                    onChange={handleAccordionChange('panel2')}
                    sx={{
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      '&:before': { display: 'none' },
                      mb: 2,
                      overflow: 'hidden',
                      '&.Mui-expanded': {
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                      },
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ 
                        backgroundColor: expandedAccordion === 'panel2' ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: 'primary.main', 
                            mr: 2,
                            width: 36,
                            height: 36
                          }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          GST Types
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: { xs: 3, md: 4 } }}>
                      <Typography sx={{ mb: 3, lineHeight: 1.7 }}>
                        GST is categorized into three types:
                      </Typography>
                      
                      <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={4}>
                          <Card sx={{ 
                            height: '100%', 
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            borderRadius: 2,
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-5px)',
                              boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                            },
                          }}>
                            <CardContent>
                              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                                CGST
                              </Typography>
                              <Typography>
                                Central GST, collected by the Central Government for intra-state transactions.
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Card sx={{ 
                            height: '100%', 
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            borderRadius: 2,
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-5px)',
                              boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                            },
                          }}>
                            <CardContent>
                              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                                SGST
                              </Typography>
                              <Typography>
                                State GST, collected by the State Government for intra-state transactions.
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Card sx={{ 
                            height: '100%', 
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            borderRadius: 2,
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                              transform: 'translateY(-5px)',
                              boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                            },
                          }}>
                            <CardContent>
                              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
                                IGST
                              </Typography>
                              <Typography>
                                Integrated GST, collected by the Central Government for inter-state transactions.
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1, color: 'success.dark' }}>
                          Have any doubt or question?
                        </Typography>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<WhatsAppIcon />}
                          href="https://wa.me/+919455133862?text=Hi!%20I%20have%20a%20question%20about%20GST%20types."
                          target="_blank"
                          sx={{ 
                            mt: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2
                          }}
                        >
                          Ask on WhatsApp
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  {/* We'll continue with the other accordions in a similar style */}
                  <Accordion 
                    expanded={expandedAccordion === 'panel3'} 
                    onChange={handleAccordionChange('panel3')}
                    sx={{
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      '&:before': { display: 'none' },
                      mb: 2,
                      overflow: 'hidden',
                      '&.Mui-expanded': {
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                      },
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ 
                        backgroundColor: expandedAccordion === 'panel3' ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: 'primary.main', 
                            mr: 2,
                            width: 36,
                            height: 36
                          }}
                        >
                          <PercentIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Current GST Rate Slabs
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: { xs: 3, md: 4 } }}>
                      <Typography sx={{ mb: 2 }}>
                        The current GST rate slabs are as follows:
                      </Typography>

                      <Box sx={{ 
                        overflowX: 'auto', 
                        mb: 4, 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <table style={{ 
                          width: '100%', 
                          borderCollapse: 'collapse', 
                          minWidth: '500px',
                        }}>
                          <thead>
                            <tr style={{ 
                              backgroundColor: theme.palette.primary.main, 
                              color: theme.palette.primary.contrastText,
                              textAlign: 'left' 
                            }}>
                              <th style={{ padding: '12px 16px', fontWeight: 600 }}>Rate</th>
                              <th style={{ padding: '12px 16px', fontWeight: 600 }}>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr style={{ 
                              backgroundColor: alpha(theme.palette.primary.main, 0.03),
                            }}>
                              <td style={{ padding: '12px 16px', fontWeight: 600 }}>0%</td>
                              <td style={{ padding: '12px 16px' }}>Essentials, basic food items</td>
                            </tr>
                            <tr>
                              <td style={{ padding: '12px 16px', fontWeight: 600 }}>5%</td>
                              <td style={{ padding: '12px 16px' }}>Household needs</td>
                            </tr>
                            <tr style={{ 
                              backgroundColor: alpha(theme.palette.primary.main, 0.03),
                            }}>
                              <td style={{ padding: '12px 16px', fontWeight: 600 }}>12%</td>
                              <td style={{ padding: '12px 16px' }}>Processed food, garments</td>
                            </tr>
                            <tr>
                              <td style={{ padding: '12px 16px', fontWeight: 600 }}>18%</td>
                              <td style={{ padding: '12px 16px' }}>Services, consumer goods</td>
                            </tr>
                            <tr style={{ 
                              backgroundColor: alpha(theme.palette.primary.main, 0.03),
                            }}>
                              <td style={{ padding: '12px 16px', fontWeight: 600 }}>28%</td>
                              <td style={{ padding: '12px 16px' }}>Luxury goods, automobiles</td>
                            </tr>
                          </tbody>
                        </table>
                      </Box>
                      
                      <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1, color: 'success.dark' }}>
                          Have any doubt or question?
                        </Typography>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<WhatsAppIcon />}
                          href="https://wa.me/+919455133862?text=Hi!%20I%20have%20a%20question%20about%20GST%20rate%20slabs."
                          target="_blank"
                          sx={{ 
                            mt: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2
                          }}
                        >
                          Ask on WhatsApp
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  {/* Additional accordions can be added here */}
                  <Accordion 
                    expanded={expandedAccordion === 'panel4'} 
                    onChange={handleAccordionChange('panel4')}
                    sx={{
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      '&:before': { display: 'none' },
                      mb: 2,
                      overflow: 'hidden',
                      '&.Mui-expanded': {
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                      },
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}
                     sx={{ 
                        backgroundColor: expandedAccordion === 'panel4' ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          sx={{ 
                            bgcolor: 'primary.main', 
                            mr: 2,
                            width: 36,
                            height: 36
                          }}
                        >
                          <ReceiptIcon fontSize="small" />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          GST on Common Goods/Services
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: { xs: 3, md: 4 } }}>
                      {/* Add more content here */}
                      {/* ... */}
                      <Typography sx={{ mb: 3 }}>
                        Below are some examples of GST rates on common goods and services:
                      </Typography>

                      <Box sx={{ 
                        overflowX: 'auto', 
                        mb: 4, 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <table style={{ 
                          width: '100%', 
                          borderCollapse: 'collapse', 
                          minWidth: '500px',
                        }}>
                          <thead>
                            <tr style={{ 
                              backgroundColor: theme.palette.primary.main, 
                              color: theme.palette.primary.contrastText,
                              textAlign: 'left' 
                            }}>
                              <th style={{ padding: '12px 16px', fontWeight: 600 }}>Item</th>
                              <th style={{ padding: '12px 16px', fontWeight: 600 }}>GST Rate</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr style={{ 
                              backgroundColor: alpha(theme.palette.primary.main, 0.03),
                            }}>
                              <td style={{ padding: '12px 16px' }}>Electronics</td>
                              <td style={{ padding: '12px 16px', fontWeight: 600 }}>18%</td>
                            </tr>
                            <tr>
                              <td style={{ padding: '12px 16px' }}>Clothes</td>
                              <td style={{ padding: '12px 16px', fontWeight: 600 }}>5% - 12%</td>
                            </tr>
                            <tr style={{ 
                              backgroundColor: alpha(theme.palette.primary.main, 0.03),
                            }}>
                              <td style={{ padding: '12px 16px' }}>Restaurants</td>
                              <td style={{ padding: '12px 16px', fontWeight: 600 }}>5%</td>
                            </tr>
                            <tr>
                              <td style={{ padding: '12px 16px' }}>Gold</td>
                              <td style={{ padding: '12px 16px', fontWeight: 600 }}>3%</td>
                            </tr>
                            <tr style={{ 
                              backgroundColor: alpha(theme.palette.primary.main, 0.03),
                            }}>
                              <td style={{ padding: '12px 16px' }}>Real Estate</td>
                              <td style={{ padding: '12px 16px', fontWeight: 600 }}>12%</td>
                            </tr>
                          </tbody>
                        </table>
                      </Box>

                      <Box sx={{ mt: 3, p: 2, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 2 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1, color: 'success.dark' }}>
                          Have any doubt or question?
                        </Typography>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<WhatsAppIcon />}
                          href="https://wa.me/+919455133862?text=Hi!%20I%20have%20a%20question%20about%20GST%20on%20common%20goods%20and%20services."
                          target="_blank"
                          sx={{ 
                            mt: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2
                          }}
                        >
                          Ask on WhatsApp
                        </Button>
                      </Box>
                    </AccordionDetails>
                  </Accordion>

                  {/* More accordions from the original code can be added here */}
                </Grid>
              </Grid>
            </Box>
          )}

          {activeTab === 1 && (
            <Box
              sx={{
                p: { xs: 2, sm: 3, md: 4 },
                bgcolor: 'background.paper',
              }}
            >
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography 
                  variant="h4" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 700, 
                    color: 'primary.main',
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' } 
                  }}
                >
                  GST Calculator
                </Typography>
                <Typography 
                  sx={{ 
                    color: 'text.secondary', 
                    maxWidth: '700px', 
                    mx: 'auto',
                    mb: 2
                  }}
                >
                  Calculate GST amounts quickly and accurately for your business transactions
                </Typography>
                <Divider sx={{ maxWidth: '150px', mx: 'auto', mb: 4, borderColor: 'secondary.main', borderBottomWidth: 3 }} />
              </Box>

              <Grid container spacing={4} justifyContent="center">
                <Grid item xs={12} md={8} lg={6}>
                  <Card
                    elevation={3}
                    sx={{
                      borderRadius: 3,
                      overflow: 'hidden',
                      transition: 'box-shadow 0.3s ease-in-out',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    <CardHeader 
                      title="GST Calculator" 
                      sx={{ 
                        textAlign: 'center', 
                        color: 'white',
                        bgcolor: 'primary.main',
                        py: 2.5
                      }}
                      titleTypographyProps={{ fontWeight: 700 }}
                    />
                    <CardContent sx={{ p: { xs: 2, md: 4 } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <FormControl fullWidth>
                          <InputLabel id="calculation-type-label">Calculation Type</InputLabel>
                          <Select
                            labelId="calculation-type-label"
                            id="calculation-type"
                            value={calculationType}
                            onChange={handleCalculationTypeChange}
                            label="Calculation Type"
                          >
                            <MenuItem value="exclusive">GST Exclusive (Add GST)</MenuItem>
                            <MenuItem value="inclusive">GST Inclusive (Extract GST)</MenuItem>
                          </Select>
                        </FormControl>
                        
                        <TextField
                          label="Amount"
                          type="number"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          fullWidth
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CurrencyRupeeIcon />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            },
                          }}
                        />
                        
                        <TextField
                          label="GST Rate (%)"
                          type="number"
                          value={gstRate}
                          onChange={(e) => setGstRate(e.target.value)}
                          fullWidth
                          variant="outlined"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PercentIcon />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title="Common GST rates: 5%, 12%, 18%, 28%">
                                  <InfoOutlinedIcon color="action" fontSize="small" />
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            },
                          }}
                        />
                        
                        <Button
                          variant="contained"
                          color="secondary"
                          onClick={handleCalculateClick}
                          fullWidth
                          size="large"
                          sx={{
                            py: 1.5,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '1rem',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            '&:hover': {
                              boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
                            },
                          }}
                        >
                          Calculate
                        </Button>
                        
                        {gstAmount !== null && totalAmount !== null && (
                          <Box 
                            sx={{ 
                              mt: 2, 
                              p: 3, 
                              border: '1px solid',
                              borderColor: 'primary.light',
                              borderRadius: 2,
                              bgcolor: alpha(theme.palette.primary.main, 0.05)
                            }}
                          >
                            <Typography 
                              variant="h6" 
                              gutterBottom 
                              sx={{ 
                                fontWeight: 700, 
                                color: 'primary.main',
                                mb: 2 
                              }}
                            >
                              Calculation Results
                            </Typography>
                            
                            <Grid container spacing={2}>
                              {calculationType === 'inclusive' && (
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                                    <Typography sx={{ fontWeight: 500 }}>Base Amount:</Typography>
                                    <Typography sx={{ fontWeight: 700 }}>
                                      ₹{(totalAmount - gstAmount).toFixed(2)}
                                    </Typography>
                                  </Box>
                                </Grid>
                              )}
                              
                              <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                                  <Typography sx={{ fontWeight: 500 }}>GST Amount ({gstRate}%):</Typography>
                                  <Typography sx={{ fontWeight: 700 }}>
                                    ₹{gstAmount.toFixed(2)}
                                  </Typography>
                                </Box>
                              </Grid>

                              {calculationType === 'exclusive' && (
                                <>
                                  <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                                      <Typography sx={{ fontWeight: 500 }}>CGST ({(parseFloat(gstRate)/2).toFixed(1)}%):</Typography>
                                      <Typography sx={{ fontWeight: 700 }}>
                                        ₹{(gstAmount / 2).toFixed(2)}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  
                                  <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                                      <Typography sx={{ fontWeight: 500 }}>SGST ({(parseFloat(gstRate)/2).toFixed(1)}%):</Typography>
                                      <Typography sx={{ fontWeight: 700 }}>
                                        ₹{(gstAmount / 2).toFixed(2)}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </>
                              )}
                              
                              <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                                  <Typography sx={{ fontWeight: 700 }}>Total Amount:</Typography>
                                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'secondary.main' }}>
                                    ₹{totalAmount.toFixed(2)}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                  
                  <Box sx={{ mt: 4 }}>
                    <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.05), borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 500 }}>
                        Note: This calculator is for estimation purposes only. For precise calculations related to your business, please consult with a tax professional.
                      </Typography>
                    </Paper>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default GSTCalculator;